import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useMutation } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { Check, Copy, Loader2, ShieldCheck, X } from "lucide-react";
import { createContext, use, useCallback, useEffect, useState } from "react";
import { trackEvent } from "@/lib/analytics";
import type { BadgeMetric, BadgeStyle, BadgeTheme } from "@/lib/badge";
import { type Currency, currencyAtom } from "@/lib/currency";
import { toast } from "@/lib/toast";
import { useTheme } from "@/utils/themeProvider";

interface ClaimWebsiteTarget {
	agentSlug: string;
	agentName: string;
	website?: string | null;
	currentStatus?: string | null;
}

interface ClaimWebsiteOptions {
	onVerified?: () => void;
}

interface ClaimWebsiteDialogContextValue {
	openClaimWebsiteDialog: (
		target: ClaimWebsiteTarget,
		options?: ClaimWebsiteOptions,
	) => void;
	closeClaimWebsiteDialog: () => void;
	isOpen: boolean;
}

const ClaimWebsiteDialogContext =
	createContext<ClaimWebsiteDialogContextValue | null>(null);

export function useClaimWebsiteDialog() {
	const context = use(ClaimWebsiteDialogContext);
	if (!context) {
		throw new Error(
			"useClaimWebsiteDialog must be used within a ClaimWebsiteDialogProvider",
		);
	}
	return context;
}

type EmbedFormat = "html" | "md";

const METRIC_OPTIONS: { value: BadgeMetric; label: string }[] = [
	{ value: "mrr", label: "MRR" },
	{ value: "alltime", label: "All-time" },
];

const STYLE_OPTIONS: { value: BadgeStyle; label: string }[] = [
	{ value: "default", label: "Default" },
	{ value: "minimal", label: "Minimal" },
	{ value: "ring", label: "Ring" },
];

const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
	{ value: "USD", label: "USD" },
	{ value: "EUR", label: "EUR" },
];

const THEME_OPTIONS: { value: BadgeTheme; label: string }[] = [
	{ value: "light", label: "Light" },
	{ value: "dark", label: "Dark" },
];

const FORMAT_OPTIONS: { value: EmbedFormat; label: string }[] = [
	{ value: "html", label: "HTML" },
	{ value: "md", label: "Markdown" },
];

const BADGE_EMBED_VERSION = "2";

export function ClaimWebsiteDialogProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [target, setTarget] = useState<ClaimWebsiteTarget | null>(null);
	const [options, setOptions] = useState<ClaimWebsiteOptions>({});

	const openClaimWebsiteDialog = useCallback(
		(next: ClaimWebsiteTarget, nextOptions: ClaimWebsiteOptions = {}) => {
			setTarget(next);
			setOptions(nextOptions);
			setIsOpen(true);
		},
		[],
	);

	const closeClaimWebsiteDialog = useCallback(() => setIsOpen(false), []);

	return (
		<ClaimWebsiteDialogContext.Provider
			value={{ openClaimWebsiteDialog, closeClaimWebsiteDialog, isOpen }}
		>
			{children}
			{target ? (
				<ClaimWebsiteDialogContent
					key={target.agentSlug}
					open={isOpen}
					onOpenChange={setIsOpen}
					target={target}
					onVerified={options.onVerified}
				/>
			) : null}
		</ClaimWebsiteDialogContext.Provider>
	);
}

function SegmentedControl<T extends string>({
	label,
	value,
	options,
	onChange,
	compact = false,
}: {
	label: string;
	value: T;
	options: { value: T; label: string }[];
	onChange: (value: T) => void;
	compact?: boolean;
}) {
	return (
		<div className={compact ? "space-y-1.5" : "flex items-center gap-3"}>
			<span
				className={
					compact
						? "block font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground"
						: "w-16 shrink-0 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground"
				}
			>
				{label}
			</span>
			<div className="flex flex-wrap gap-1">
				{options.map((option) => {
					const selected = option.value === value;
					return (
						<button
							key={option.value}
							type="button"
							onClick={() => onChange(option.value)}
							className={`inline-flex items-center border font-mono uppercase tracking-[0.12em] transition-[color,border-color,background-color,transform] active:scale-[0.96] ${
								compact ? "min-h-8 px-2.5 text-xs" : "min-h-9 px-3 text-sm"
							} ${
								selected
									? "border-brand bg-brand-soft text-brand"
									: "border-border bg-background text-muted-foreground hover:border-brand/40 hover:text-foreground"
							}`}
						>
							{option.label}
						</button>
					);
				})}
			</div>
		</div>
	);
}

function getSystemBadgeTheme(): BadgeTheme {
	if (typeof window === "undefined") return "dark";
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

function useActiveBadgeTheme(): BadgeTheme {
	const { theme: appTheme } = useTheme();
	const [systemTheme, setSystemTheme] =
		useState<BadgeTheme>(getSystemBadgeTheme);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const query = window.matchMedia("(prefers-color-scheme: dark)");
		const update = () => setSystemTheme(query.matches ? "dark" : "light");
		update();
		query.addEventListener("change", update);
		return () => query.removeEventListener("change", update);
	}, []);

	return appTheme === "light" || appTheme === "dark" ? appTheme : systemTheme;
}

export function BadgeVerificationPanel({
	target,
	onVerified,
	className = "space-y-7 px-5 py-5",
}: {
	target: ClaimWebsiteTarget;
	onVerified?: () => void;
	className?: string;
}) {
	const [metric, setMetric] = useState<BadgeMetric>("mrr");
	const [style, setStyle] = useState<BadgeStyle>("default");
	const [format, setFormat] = useState<EmbedFormat>("html");
	const [copied, setCopied] = useState(false);
	const [verified, setVerified] = useState(target.currentStatus === "verified");
	// Default to the viewer's active theme, then let them switch explicitly.
	const activeTheme = useActiveBadgeTheme();
	const [theme, setTheme] = useState<BadgeTheme>(activeTheme);
	const [currency, setCurrency] = useAtom(currencyAtom);
	const [origin] = useState(() =>
		typeof window !== "undefined" ? window.location.origin : "",
	);

	const slug = target.agentSlug;
	const profileUrl = `${origin}/agents/${slug}`;
	const badgeUrl = `${origin}/api/listed-badge/${slug}?metric=${metric}&style=${style}&theme=${theme}&currency=${currency}&v=${BADGE_EMBED_VERSION}`;
	const alt = `${target.agentName} on AgentMRR`;
	const snippet =
		format === "md"
			? `[![${alt}](${badgeUrl})](${profileUrl})`
			: `<a href="${profileUrl}" target="_blank" rel="noreferrer"><img src="${badgeUrl}" alt="${alt}" height="20" /></a>`;

	const verifyMutation = useMutation({
		mutationFn: async () => {
			const response = await fetch("/api/agents/claim-website", {
				body: JSON.stringify({ agentSlug: slug }),
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				method: "POST",
			});
			const payload = (await response.json().catch(() => null)) as {
				error?: string;
				websiteVerificationStatus?: string;
				websiteVerificationError?: string | null;
			} | null;
			if (!response.ok) {
				throw new Error(payload?.error || "Failed to verify website");
			}
			return payload ?? {};
		},
		onSuccess: (data) => {
			if (data.websiteVerificationStatus === "verified") {
				setVerified(true);
				toast.success("Website verified", {
					description: "AgentMRR found the badge on your site.",
				});
				onVerified?.();
			} else {
				toast.error("Verification failed", {
					description:
						data.websiteVerificationError ??
						"We couldn't find the badge embed. Add it to your homepage and try again.",
				});
			}
		},
		onError: (error) => {
			toast.error("Verification failed", {
				description: error instanceof Error ? error.message : undefined,
			});
		},
	});

	function handleCopy() {
		navigator.clipboard.writeText(snippet);
		trackEvent("agent_badge_copied", {
			agent_slug: slug,
			format: "embed",
			surface: "claim-dialog",
		});
		setCopied(true);
		window.setTimeout(() => setCopied(false), 2000);
	}

	return (
		<div className={className}>
			{verified ? (
				<div className="flex items-center gap-2 border border-brand/30 bg-brand-soft px-4 py-3 font-mono text-sm uppercase tracking-[0.12em] text-brand">
					<ShieldCheck className="size-4" />
					Website verified
				</div>
			) : null}

			<section className="space-y-3">
				<h3 className="font-mono text-sm uppercase tracking-[0.14em] text-foreground">
					1. Customize badge
				</h3>
				<div className="grid gap-3 sm:grid-cols-2">
					<SegmentedControl
						compact
						label="Metric"
						value={metric}
						options={METRIC_OPTIONS}
						onChange={setMetric}
					/>
					<SegmentedControl
						compact
						label="Currency"
						value={currency}
						options={CURRENCY_OPTIONS}
						onChange={setCurrency}
					/>
					<SegmentedControl
						compact
						label="Theme"
						value={theme}
						options={THEME_OPTIONS}
						onChange={setTheme}
					/>
					<SegmentedControl
						compact
						label="Style"
						value={style}
						options={STYLE_OPTIONS}
						onChange={setStyle}
					/>
				</div>
				<div
					className="flex items-center justify-center border border-border px-6 py-8"
					style={{ background: theme === "dark" ? "#0a0a0a" : "#ffffff" }}
				>
					<img src={badgeUrl} alt={alt} className="h-7" />
				</div>
			</section>

			<section className="space-y-3">
				<h3 className="font-mono text-sm uppercase tracking-[0.14em] text-foreground">
					2. Copy embed code
				</h3>
				<SegmentedControl
					label="Format"
					value={format}
					options={FORMAT_OPTIONS}
					onChange={setFormat}
				/>
				<button
					type="button"
					onClick={handleCopy}
					className="inline-flex min-h-11 w-full items-center justify-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-[color,border-color,transform] hover:border-brand/40 hover:text-brand active:scale-[0.96]"
				>
					{copied ? (
						<Check className="size-3.5 text-brand" />
					) : (
						<Copy className="size-3.5" />
					)}
					{copied ? "Embed code copied" : "Copy embed code"}
				</button>
				{copied ? (
					<div className="border border-brand/30 bg-brand-soft px-4 py-3 font-mono text-sm uppercase tracking-[0.12em] text-brand">
						Paste it into your site, then verify the badge below
					</div>
				) : null}
			</section>

			<section className="space-y-3">
				<h3 className="font-mono text-sm uppercase tracking-[0.14em] text-foreground">
					3. Verify badge
				</h3>
				<ul className="space-y-1.5 font-mono text-sm text-muted-foreground">
					<li className="flex gap-2">
						<span className="text-brand">·</span>
						Badge updates automatically when your revenue changes
					</li>
					<li className="flex gap-2">
						<span className="text-brand">·</span>
						Clicking the badge links to your AgentMRR profile
					</li>
				</ul>
				<button
					type="button"
					onClick={() => verifyMutation.mutate()}
					disabled={verifyMutation.isPending}
					className="inline-flex min-h-11 w-full items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-brand-hover active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40"
				>
					{verifyMutation.isPending ? (
						<>
							<Loader2 className="size-3.5 animate-spin" />
							Checking website
						</>
					) : verified ? (
						"Re-verify badge"
					) : (
						"Verify badge"
					)}
				</button>
			</section>
		</div>
	);
}

function ClaimWebsiteDialogContent({
	open,
	onOpenChange,
	target,
	onVerified,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	target: ClaimWebsiteTarget;
	onVerified?: () => void;
}) {
	return (
		<DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] px-4 duration-200 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
					<div className="max-h-[90vh] overflow-y-auto border border-border bg-card text-card-foreground">
						<div className="flex items-center justify-between border-b border-border px-5 py-3">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Verify website ownership
							</span>
							<DialogPrimitive.Close className="-mr-2 inline-flex size-10 items-center justify-center font-mono text-sm text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96]">
								<X className="size-3.5" />
								<span className="sr-only">Close</span>
							</DialogPrimitive.Close>
						</div>

						<DialogPrimitive.Title className="sr-only">
							Claim website ownership for {target.agentName}
						</DialogPrimitive.Title>
						<DialogPrimitive.Description className="sr-only">
							Add the AgentMRR badge to your website to verify ownership.
						</DialogPrimitive.Description>

						<BadgeVerificationPanel target={target} onVerified={onVerified} />
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
